import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type AffiliateCommissionRow,
  COMMISSION_STATUSES,
  formatCommissionAmount,
  maskReferredUserId,
} from "@/lib/affiliateDashboard";

const STATUS_VARIANT: Record<
  AffiliateCommissionRow["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  approved: "default",
  paid: "default",
  reversed: "destructive",
  forfeited: "outline",
};

export type OverrideAction = "approve" | "reverse" | "forfeit" | "mark_paid";

type Props = {
  rows: AffiliateCommissionRow[];
  rowsLoading: boolean;
  isSuperAdmin: boolean;
  statusFilter: AffiliateCommissionRow["status"] | "all";
  setStatusFilter: (v: AffiliateCommissionRow["status"] | "all") => void;
  classFilter: "standard" | "super" | "all";
  setClassFilter: (v: "standard" | "super" | "all") => void;
  onOverride: (row: AffiliateCommissionRow, action: OverrideAction) => void;
};

export function AffiliateLedgerCard({
  rows,
  rowsLoading,
  isSuperAdmin,
  statusFilter,
  setStatusFilter,
  classFilter,
  setClassFilter,
  onOverride,
}: Props) {
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle>Recent commissions</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={classFilter}
              onValueChange={(v) => setClassFilter(v as "standard" | "super" | "all")}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard only</SelectItem>
                <SelectItem value="super">Super Affiliates only</SelectItem>
                <SelectItem value="all">All affiliates</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as AffiliateCommissionRow["status"] | "all")
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {COMMISSION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Accrued</TableHead>
                <TableHead>Affiliate</TableHead>
                <TableHead>Referred</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead>Status</TableHead>
                {isSuperAdmin ? <TableHead className="text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowsLoading ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-center text-muted-foreground">
                    No commissions match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {new Date(row.accrued_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <span className="inline-flex items-center gap-1">
                        {maskReferredUserId(row.affiliate_user_id)}
                        {row.is_super_affiliate ? (
                          <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary">
                            Super
                          </span>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {maskReferredUserId(row.referred_user_id)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap font-medium">
                      {formatCommissionAmount(row.commission_amount_cents, row.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status]} className="capitalize">
                        {row.status}
                      </Badge>
                    </TableCell>
                    {isSuperAdmin ? (
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          {row.status === "pending" ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onOverride(row, "approve")}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onOverride(row, "forfeit")}
                              >
                                Forfeit
                              </Button>
                            </>
                          ) : null}
                          {row.status === "approved" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onOverride(row, "mark_paid")}
                            >
                              Mark paid
                            </Button>
                          ) : null}
                          {(row.status === "pending" ||
                            row.status === "approved" ||
                            row.status === "paid") ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onOverride(row, "reverse")}
                            >
                              Reverse
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
